import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../shared/i18n';
import apiClient from '../api/client';

/**
 * AddGuardian - Form to add a new guardian to the family
 */
export const AddGuardian = ({ familyName, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('guardian');
  const [error, setError] = useState(null);

  const addGuardianMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('/guardians/family_users', {
        guardian: data
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate dashboard query to refresh family_users
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      if (onSuccess) {
        onSuccess(data.message);
      }
    },
    onError: (err) => {
      const errorMessage = err.response?.data?.error?.message || t('guardians.add.error_generic');
      setError(errorMessage);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    addGuardianMutation.mutate({ email, role });
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/guardians');
    }
  };

  return (
    <div className="bg-light min-vh-100">
      <div className="container py-4">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="display-6 fw-bold mb-2">
            {t('guardians.add.title', { family: familyName })}
          </h1>
          <p className="text-muted">{t('guardians.add.subtitle')}</p>
        </div>

        <div className="row g-4">
          {/* Main Column - Add Guardian Form */}
          <div className="col-lg-8">
            <div className="card border rounded-3 shadow-sm">
              <div className="card-body p-5">
                {/* Icon and Header */}
                <div className="text-center mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '96px',
                      height: '96px',
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                    }}
                  >
                    <i className="bi bi-person-plus-fill" style={{ fontSize: '45px', color: '#16a34a' }}></i>
                  </div>
                  <h2 className="h3 fw-bold mb-3">{t('guardians.add.form_title')}</h2>
                  <p className="text-muted mb-0">{t('guardians.add.form_description')}</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="mt-4">
                  {/* Error Message */}
                  {error && (
                    <div className="alert alert-danger mb-4" data-testid="add-guardian-error">
                      {error}
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="mb-4">
                    <label htmlFor="guardian_email" className="form-label fw-semibold">
                      {t('guardians.add.email_label')}
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="guardian_email"
                      name="guardian_email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('guardians.add.email_placeholder')}
                      style={{ height: '50px', borderRadius: '8px' }}
                      required
                      disabled={addGuardianMutation.isPending}
                    />
                    <div className="form-text">{t('guardians.add.email_help')}</div>
                  </div>

                  {/* Role Selection */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">{t('guardians.add.role_label')}</label>
                    <div className="d-flex flex-column gap-3">
                      <div className="form-check p-3 border rounded-3">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="role_owner"
                          name="role"
                          value="owner"
                          checked={role === 'owner'}
                          onChange={(e) => setRole(e.target.value)}
                          disabled={addGuardianMutation.isPending}
                        />
                        <label className="form-check-label w-100" htmlFor="role_owner">
                          <div className="fw-semibold">{t('guardians.add.role_owner')}</div>
                          <div className="text-muted small">{t('guardians.add.role_owner_description')}</div>
                        </label>
                      </div>
                      <div className="form-check p-3 border rounded-3">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="role_guardian"
                          name="role"
                          value="guardian"
                          checked={role === 'guardian'}
                          onChange={(e) => setRole(e.target.value)}
                          disabled={addGuardianMutation.isPending}
                        />
                        <label className="form-check-label w-100" htmlFor="role_guardian">
                          <div className="fw-semibold">{t('guardians.add.role_guardian')}</div>
                          <div className="text-muted small">{t('guardians.add.role_guardian_description')}</div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="btn btn-lg btn-outline-secondary px-4 py-3"
                      style={{ borderRadius: '12px' }}
                      disabled={addGuardianMutation.isPending}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-lg text-white fw-semibold px-5 py-3"
                      style={{
                        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                        borderRadius: '12px',
                        border: 'none'
                      }}
                      disabled={addGuardianMutation.isPending}
                    >
                      {addGuardianMutation.isPending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t('common.loading')}
                        </>
                      ) : (
                        t('guardians.add.submit')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* About Roles Card */}
            <div
              className="card border-0 rounded-3 shadow-sm mb-4 text-white"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}
            >
              <div className="card-body p-4">
                <h3 className="h5 fw-bold mb-3">{t('guardians.add.roles_info_title')}</h3>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="fw-semibold mb-1">{t('guardians.add.role_owner')}</div>
                    <div style={{ fontSize: '13px', opacity: 0.9 }}>
                      {t('guardians.add.roles_info_owner')}
                    </div>
                  </div>
                  <div>
                    <div className="fw-semibold mb-1">{t('guardians.add.role_guardian')}</div>
                    <div style={{ fontSize: '13px', opacity: 0.9 }}>
                      {t('guardians.add.roles_info_guardian')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tips Card */}
            <div className="card border rounded-3 shadow-sm mb-4">
              <div className="card-body p-4">
                <h3 className="h5 fw-bold mb-3">{t('guardians.add.tips_title')}</h3>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 me-3 flex-shrink-0"
                      style={{ width: '32px', height: '32px', backgroundColor: '#dbeafe' }}
                    >
                      <i className="bi bi-info-circle-fill" style={{ fontSize: '14px', color: '#3b82f6' }}></i>
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '13px' }}>
                        {t('guardians.add.tip_account_required')}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 me-3 flex-shrink-0"
                      style={{ width: '32px', height: '32px', backgroundColor: '#dcfce7' }}
                    >
                      <i className="bi bi-shield-check" style={{ fontSize: '14px', color: '#16a34a' }}></i>
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '13px' }}>
                        {t('guardians.add.tip_multiple_guardians')}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 me-3 flex-shrink-0"
                      style={{ width: '32px', height: '32px', backgroundColor: '#fef3c7' }}
                    >
                      <i className="bi bi-person-check-fill" style={{ fontSize: '14px', color: '#ca8a04' }}></i>
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '13px' }}>
                        {t('guardians.add.tip_owner_for_coparents')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddGuardian;
